import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98] ring-offset-background',
  {
    variants: {
      variant: {
        default: 'brand-button-bg text-primary-foreground hover:-translate-y-0.5 hover:bg-primary-hover hover:shadow-glow',
        destructive: 'bg-red-600 text-white shadow-md hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-lg dark:bg-red-500 dark:hover:bg-red-400',
        outline: 'border border-border bg-card-background text-text-dark shadow-sm hover:-translate-y-0.5 hover:border-primary hover:bg-[color:var(--color-blue-50)] hover:text-primary-hover dark:hover:bg-[color:var(--color-surface-muted)]',
        secondary: 'bg-accent text-accent-foreground shadow-sm hover:-translate-y-0.5 hover:bg-success hover:text-white hover:shadow-md',
        ghost: 'text-text-dark hover:bg-[color:var(--color-blue-50)] hover:text-primary-hover dark:hover:bg-[color:var(--color-surface-muted)]',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-6 py-3 text-sm',
        sm: 'h-9 rounded-2xl px-4 text-xs',
        lg: 'h-12 rounded-2xl px-8 text-base',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    void asChild;
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
